import * as THREE from "three";

/**
 * CharacterAnimator drives a character model's clips through a tiny state
 * machine: idle / walk / attack / die. Clips can come from the model itself
 * (Kenney characters embed them) or from an external library retargeted by
 * bone name (KayKit characters keep clips in a separate rig file). Missing
 * states are simply skipped, so a model with only "idle" still works.
 */
export class CharacterAnimator {
  constructor(model, clips) {
    this.mixer = new THREE.AnimationMixer(model);
    this.actions = {};
    this.active = null;
    this.current = null;
    this.locked = false; // true while a one-shot (attack/die) is playing

    // NOTE: never match "static" — in Kenney packs that clip is the literal
    // T-pose. Idle must be a real idle clip.
    const find = (re) => clips.find((c) => re.test(c.name) && !/static|t-?pose/i.test(c.name));
    this.map = {
      idle: find(/idle|breath|stand/i),
      walk: find(/walk|run|sprint|move/i),
      attack: find(/attack|hit|slash|swing|melee|interact/i),
      die: find(/death|die|dead/i),
    };

    this.mixer.addEventListener("finished", (e) => {
      if (this.active && e.action === this.active && this.locked) {
        if (this._holdOnFinish) return; // e.g. death pose stays
        this.locked = false;
        this.current = null;
        this._to(this._wantMoving ? "walk" : "idle");
      }
    });

    this._to("idle");
  }

  _get(name) {
    const clip = this.map[name];
    if (!clip) return null;
    if (!this.actions[name]) this.actions[name] = this.mixer.clipAction(clip);
    return this.actions[name];
  }

  _to(name, { loop = true, fade = 0.2 } = {}) {
    const next = this._get(name) || this._get("idle");
    if (!next) return;
    if (this.active === next && this.current === name) return;
    next.reset();
    next.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
    next.clampWhenFinished = !loop;
    if (this.active && this.active !== next) this.active.fadeOut(fade);
    next.fadeIn(fade).play();
    this.active = next;
    this.current = name;
  }

  /** Loop idle or walk based on movement (ignored while a one-shot plays). */
  setMoving(moving) {
    this._wantMoving = moving;
    if (this.locked) return;
    this._to(moving && this.map.walk ? "walk" : "idle");
  }

  /** Play attack/die once, then fall back to idle/walk (hold=true clamps, e.g. death). */
  oneShot(name, { hold = false } = {}) {
    const act = this._get(name);
    if (!act) return false;
    this._to(name, { loop: false, fade: 0.08 });
    this.locked = true;
    this._holdOnFinish = hold;
    return true;
  }

  update(dt) { this.mixer.update(dt); }
}
